import { useEffect, useState } from "react";
import { ClassTransport } from "./ClassTransport";
import type { ClassClient, ClassJoinOpts, ClassRoomState } from "./types";

/**
 * Connect to a class session for the lifetime of the page. `opts` must be
 * memoised (stable code/role/id) or the socket reconnects every render.
 */
export function useClassRoom(opts: ClassJoinOpts | null): {
  client: ClassClient | null;
  state: ClassRoomState | null;
  error: string | null;
} {
  const [client, setClient] = useState<ClassClient | null>(null);
  const [state, setState] = useState<ClassRoomState | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opts) return;
    const c = new ClassTransport();
    setClient(c);
    const off = c.on((e) => {
      if (e.type === "state") {
        setError(null);
        setState((prev) => {
          const nr = e.state.rev, cr = prev?.rev;
          if (typeof nr === "number" && typeof cr === "number" && nr <= cr) return prev;
          return e.state;
        });
      } else if (e.type === "error") {
        setError(e.message);
      }
    });
    c.join(opts).then(setState).catch((err) => setError((err as Error).message ?? "Could not join class"));
    return () => { off(); c.leave(); setClient(null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts?.code, opts?.role, opts?.studentId]);

  return { client, state, error };
}
